import { useMemo, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Loader2 } from 'lucide-react'
import { useAccountAnalytics } from './hooks/useReconApi'
import type { AnalyticFieldDef } from './types'

interface APXFieldsEditorProps {
  clientId: string
  accountId: number
  values: Record<string, string>
  onChange: (values: Record<string, string>, partStr: string) => void
}

function buildPartString(fields: AnalyticFieldDef[], values: Record<string, string>): string {
  return (
    fields
      .sort((a, b) => a.row_num - b.row_num)
      .map((f) => values[f.field_name] || '')
      .join(';') + ';'
  )
}

export function APXFieldsEditor({ clientId, accountId, values, onChange }: APXFieldsEditorProps) {
  const { data: def, isLoading } = useAccountAnalytics(clientId, accountId)

  const handleFieldChange = (fieldName: string, value: string) => {
    const next = { ...values, [fieldName]: value }
    const partStr = def ? buildPartString(def.fields, next) : ''
    onChange(next, partStr)
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-neutral-400">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading analytics...
      </div>
    )
  }

  if (!def || def.fields.length === 0) return null

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3">
      <div className="mb-2 text-xs font-medium text-amber-700">Analytic parts</div>
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(def.fields.length, 3)}, 1fr)` }}>
        {def.fields
          .sort((a, b) => a.row_num - b.row_num)
          .map((field) => (
            <AnalyticField
              key={field.field_id}
              field={field}
              value={values[field.field_name] || ''}
              onChange={(v) => handleFieldChange(field.field_name, v)}
            />
          ))}
      </div>
    </div>
  )
}

function AnalyticField({
  field,
  value,
  onChange,
}: {
  field: AnalyticFieldDef
  value: string
  onChange: (v: string) => void
}) {
  const [search, setSearch] = useState('')

  const filteredOptions = useMemo(() => {
    if (!field.options) return []
    if (!search) return field.options.slice(0, 50)
    const q = search.toLowerCase()
    return field.options.filter(
      (o) =>
        o.code.toLowerCase().includes(q) ||
        o.description.toLowerCase().includes(q)
    ).slice(0, 50)
  }, [field.options, search])

  const selectedLabel = useMemo(() => {
    if (!field.is_lookup || !field.options || !value) return null
    const opt = field.options.find((o) => String(o.id) === value)
    return opt ? `${opt.code} — ${opt.description}` : null
  }, [field, value])

  if (field.is_lookup && field.options) {
    return (
      <div>
        <label className="mb-1 block text-xs text-neutral-600">
          {field.label}
          {field.is_required && <span className="text-red-500"> *</span>}
        </label>
        {selectedLabel ? (
          <div className="flex items-center gap-1">
            <span className="flex-1 truncate rounded border bg-white px-2 py-1 text-sm">
              {selectedLabel}
            </span>
            <button
              type="button"
              className="text-xs text-neutral-400 hover:text-neutral-600"
              onClick={() => onChange('')}
            >
              ×
            </button>
          </div>
        ) : (
          <>
            <Input
              placeholder={`Search ${field.label.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
            />
            {(search || filteredOptions.length <= 10) && (
              <div className="mt-1 max-h-[120px] overflow-y-auto rounded border bg-white text-sm">
                {filteredOptions.length === 0 ? (
                  <div className="px-2 py-1 text-xs text-neutral-400">No matches</div>
                ) : (
                  filteredOptions.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-blue-50"
                      onClick={() => {
                        onChange(String(o.id))
                        setSearch('')
                      }}
                    >
                      <span className="font-mono text-xs text-neutral-500">{o.code}</span>
                      <span className="truncate">{o.description}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      <label className="mb-1 block text-xs text-neutral-600">
        {field.label}
        {field.is_required && <span className="text-red-500"> *</span>}
      </label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.label}
        className="h-8 text-sm"
      />
    </div>
  )
}
